import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	Collapse,
	Empty,
	Flex,
	Input,
	InputNumber,
	Modal,
	Select,
	Space,
	Tag,
	message,
} from 'antd';
import { CheckCircleFilled, SearchOutlined } from '@ant-design/icons';

import {
	RobotProgramFinding,
	VSCPartData,
	VSCPartJson,
	YPushHeightSuggestion,
} from '../../../interfaces/VersaBuilt.Interface';
import {
	useCheckVSCPartDataQuery,
	useCreateVSCPartDataMutation,
	useUpdateVSCPartDataMutation,
} from '../../../redux/api/versabuilt';
import { REQUIRED_NEW_PROGRAM_KEYS } from './programTemplate';
import { extractApiError } from '../../../utils/apiError';
import StockDimensionHelp from './StockDimensionHelp';
import UnitSwitch from './UnitSwitch';
import CamStockAutofill, { CamStockValues } from './CamStockAutofill';
import {
	LENGTH_PARAMETER_UNITS,
	STOCK_DIMENSION_KEYS,
	formatLength,
	fromInches,
	toInches,
} from './units';
import {
	HIDDEN_PARAMETER_KEYS,
	HIDDEN_ROOT_KEYS,
	JAW_OPTIONS,
	OP_COUNT_OPTIONS,
	VIEW_LAYOUT,
	VISE_OPTIONS,
	ViewField,
	ViewFieldKind,
} from './viewLayout';

const KG_PER_LB = 0.45359237;

// The audit rules live on the server; wait for a pause in typing before asking.
const CHECK_DELAY_MS = 300;

// Shown to at least as many places as anyone types; stored values stay metric
// at full precision, so rounding here never reaches the database.
const DISPLAY_DECIMALS = { in: 4, mm: 3, lb: 3, kg: 4 };

// The top-level `jaws` list mirrors the per-op jaw names the VSC reads.
const JAW_KEY_INDEX: Record<string, number> = { op1Jaws: 0, op2Jaws: 1 };

/** One `jaws` entry per op; an added op takes its Op N Jaws parameter. */
const resizeJaws = (json: VSCPartJson, opCount: number) => {
	if (!Array.isArray(json.jaws)) {
		return json.jaws;
	}
	const jaws = json.jaws.slice(0, opCount);
	const parameters = (json.parameters ?? {}) as Record<string, unknown>;
	for (let op = jaws.length + 1; op <= opCount; op++) {
		const opJaws = parameters[`op${op}Jaws`];
		jaws.push(typeof opJaws === 'string' ? opJaws : jaws[jaws.length - 1]);
	}
	return jaws;
};

interface ResolvedField {
	label: string;
	key: string;
	kind: ViewFieldKind;
	root: boolean;
	value: unknown;
}

interface ResolvedSection {
	title?: string;
	rows: ResolvedField[][];
}

/** `op1to2TransferHeight` -> `Op1to2 Transfer Height`, `op1YPush` -> `Op1 Y Push`. */
const humanize = (key: string) =>
	key
		.replace(/_/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
		.replace(/^./, (c) => c.toUpperCase());

const round = (value: number, decimals: number) =>
	Number(value.toFixed(decimals));

/** Robot pose arrays the VSC stores as six joint values. */
const ROBOT_POSE_LENGTH = 6;

const normalizeNumberArray = (value: unknown): number[] =>
	Array.from({ length: ROBOT_POSE_LENGTH }, (_, index) => {
		const item = Array.isArray(value) ? value[index] : undefined;
		return typeof item === 'number' && Number.isFinite(item) ? item : 0;
	});

const inferKind = (key: string, value: unknown): ViewFieldKind => {
	if (typeof value === 'boolean') return 'checkbox';
	if (key in LENGTH_PARAMETER_UNITS) return 'length';
	if (typeof value === 'number' && /weight/i.test(key)) return 'weight';
	if (typeof value === 'number') return 'number';
	return 'text';
};

const isStructured = (value: unknown) =>
	value !== null && typeof value === 'object';

/** The VSC's own layout, then whatever it doesn't show so nothing is hidden. */
const resolveSections = (
	json: VSCPartJson,
	original: VSCPartJson
): ResolvedSection[] => {
	const parameters = (json.parameters ?? {}) as Record<string, unknown>;
	const originalParameters = (original.parameters ?? {}) as Record<
		string,
		unknown
	>;
	const usedRoot = new Set(['parameters']);
	const usedParameters = new Set<string>();
	const opCount = typeof json.op_count === 'number' ? json.op_count : 2;

	const resolve = (field: ViewField): ResolvedField | null => {
		const source = field.root ? json : parameters;
		const key = field.keys.find((k) => k in source);
		if (!key) return null;
		// Marked used even when hidden, so it doesn't reappear in Other Parameters.
		(field.root ? usedRoot : usedParameters).add(key);
		if (field.showWhen?.some((dependency) => !parameters[dependency])) {
			return null;
		}
		if (field.minOps !== undefined && opCount < field.minOps) {
			return null;
		}
		return {
			label: field.label,
			key,
			kind: field.kind,
			root: !!field.root,
			value: source[key],
		};
	};

	const sections: ResolvedSection[] = VIEW_LAYOUT.map((section) => {
		const rows = section.rows
			.map((row) =>
				row.map(resolve).filter((f): f is ResolvedField => f !== null)
			)
			.filter((row) => row.length > 0);
		// Resolved anyway so its keys stay out of Other Parameters.
		const hidden = section.minOps !== undefined && opCount < section.minOps;
		return { title: section.title, rows: hidden ? [] : rows };
	}).filter((section) => section.rows.length > 0);

	const leftovers: ResolvedField[] = [
		...Object.keys(json)
			.filter((key) => !usedRoot.has(key) && !HIDDEN_ROOT_KEYS.has(key))
			.map((key) => ({
				label: humanize(key),
				key,
				// From the stored value, so clearing a number doesn't turn it into text.
				kind: inferKind(key, original[key] ?? json[key]),
				root: true,
				value: json[key],
			})),
		...Object.keys(parameters)
			.filter(
				(key) => !usedParameters.has(key) && !HIDDEN_PARAMETER_KEYS.has(key)
			)
			.sort()
			.map((key) => ({
				label: humanize(key),
				key,
				kind: inferKind(key, originalParameters[key] ?? parameters[key]),
				root: false,
				value: parameters[key],
			})),
	];

	if (leftovers.length) {
		const rows: ResolvedField[][] = [];
		for (let i = 0; i < leftovers.length; i += 4) {
			rows.push(leftovers.slice(i, i + 4));
		}
		sections.push({ title: 'Other Parameters', rows });
	}
	return sections;
};

/**
 * Wraps a row onto more lines as the modal narrows instead of squeezing it.
 * `auto-fit` collapses unused tracks, so on a wide modal every field in the
 * row still shares the full width. Checkbox-only rows get narrower columns so
 * the seven jaw-cleaning options stay on one line on desktop. Below the `md`
 * breakpoint the row's `max-md:!grid-cols-1` class overrides this entirely.
 */
const rowColumns = (row: ResolvedField[]) => {
	const minWidth = row.every((field) => field.kind === 'checkbox') ? 130 : 180;
	return `repeat(auto-fit, minmax(min(100%, ${minWidth}px), 1fr))`;
};

const FieldLabel = ({
	field,
	hint,
}: {
	field: ResolvedField;
	hint?: string;
}) => (
	<div className="font-semibold text-sm mb-1" title={field.key}>
		{field.label}
		{hint && (
			<span className="ml-1 text-xs font-normal text-slate-500">({hint})</span>
		)}
		{!field.root && <StockDimensionHelp parameterKey={field.key} />}
	</div>
);

const IssueText = ({
	issue,
	value,
}: {
	issue?: RobotProgramFinding;
	value: unknown;
}) =>
	issue ? (
		<div
			className="text-xs mt-1"
			style={{ color: issue.severity === 'error' ? '#cf1322' : '#d48806' }}
		>
			{issue.message}
			{/* A non-number can't be shown in a number input, so say what's stored. */}
			{typeof value !== 'number' && ` (stored as ${JSON.stringify(value)})`}
		</div>
	) : null;

interface FieldInputProps {
	field: ResolvedField;
	useInches: boolean;
	readOnly: boolean;
	issue?: RobotProgramFinding;
	suggestion?: YPushHeightSuggestion | null;
	onApplySuggestion?: () => void;
	onChange: (value: unknown) => void;
}

const FieldInput = ({
	field,
	useInches,
	readOnly,
	issue,
	suggestion,
	onApplySuggestion,
	onChange,
}: FieldInputProps) => {
	const { kind, value, key } = field;
	const issueStatus = issue?.severity;

	if (kind === 'checkbox') {
		return (
			<Checkbox
				checked={!!value}
				disabled={readOnly}
				title={key}
				onChange={(e) => onChange(e.target.checked)}
			>
				{field.label}
			</Checkbox>
		);
	}

	if (kind === 'jaws' || kind === 'vise' || kind === 'ops') {
		const options: (string | number)[] =
			kind === 'jaws'
				? JAW_OPTIONS
				: kind === 'vise'
					? VISE_OPTIONS
					: OP_COUNT_OPTIONS;
		// Keep a value from outside the list selectable instead of silently dropping it.
		const all =
			value !== null && value !== undefined && !options.includes(value as never)
				? [value as string | number, ...options]
				: options;
		return (
			<>
				<FieldLabel field={field} />
				<Select
					value={value ?? undefined}
					placeholder="—"
					disabled={readOnly}
					onChange={onChange}
					options={all.map((option) => ({
						label: String(option),
						value: option,
					}))}
					style={{ width: 270, maxWidth: '100%' }}
				/>
			</>
		);
	}

	if (kind === 'numberArray') {
		const items = normalizeNumberArray(value);
		return (
			<>
				<FieldLabel field={field} />
				<div
					className="grid gap-2 max-md:!grid-cols-3"
					style={{ gridTemplateColumns: `repeat(${ROBOT_POSE_LENGTH}, minmax(0, 1fr))` }}
				>
					{items.map((item, index) => (
						<InputNumber
							key={index}
							style={{ width: '100%' }}
							readOnly={readOnly}
							value={item}
							onChange={(next) => {
								const updated = [...items];
								updated[index] = next === null ? 0 : Number(next);
								onChange(updated);
							}}
						/>
					))}
				</div>
			</>
		);
	}

	if (isStructured(value)) {
		return (
			<>
				<FieldLabel field={field} />
				<Input
					readOnly
					value={
						Array.isArray(value) && value.every((v) => typeof v !== 'object')
							? value.join(', ')
							: JSON.stringify(value)
					}
					title="Edit lists in the JSON editor"
				/>
			</>
		);
	}

	if (
		kind === 'length' ||
		kind === 'weight' ||
		kind === 'number' ||
		typeof value === 'number'
	) {
		let hint: string | undefined;
		let toDisplay = (v: number) => v;
		let fromDisplay = (v: number) => v;

		if (kind === 'length') {
			const unit = LENGTH_PARAMETER_UNITS[key] ?? 'mm';
			hint = useInches ? 'Inches' : 'mm';
			toDisplay = useInches
				? (v) => round(toInches(v, unit), DISPLAY_DECIMALS.in)
				: (v) => round(v, DISPLAY_DECIMALS.mm);
			fromDisplay = useInches ? (v) => fromInches(v, unit) : (v) => v;
		} else if (kind === 'weight') {
			hint = useInches ? 'Pounds' : 'kg';
			toDisplay = useInches
				? (v) => round(v / KG_PER_LB, DISPLAY_DECIMALS.lb)
				: (v) => round(v, DISPLAY_DECIMALS.kg);
			fromDisplay = useInches ? (v) => v * KG_PER_LB : (v) => v;
		}

		const positiveOnly = kind === 'weight' || STOCK_DIMENSION_KEYS.has(key);
		const positiveMinDisplay = positiveOnly
			? 10 **
				-(kind === 'weight'
					? useInches
						? DISPLAY_DECIMALS.lb
						: DISPLAY_DECIMALS.kg
					: useInches
						? DISPLAY_DECIMALS.in
						: DISPLAY_DECIMALS.mm)
			: undefined;

		const hasSuggestion = !!suggestion;
		const matchesSuggestion = !!suggestion?.matches;
		const showSuggestionApply =
			hasSuggestion && !matchesSuggestion && !!onApplySuggestion && !readOnly;
		const showSuggestionAddon = matchesSuggestion || showSuggestionApply;

		const inputNumber = (
			<InputNumber
				style={{ width: '100%' }}
				readOnly={readOnly}
				controls={!showSuggestionAddon}
				min={positiveMinDisplay}
				status={issueStatus ?? (value === null ? 'error' : undefined)}
				value={typeof value === 'number' ? toDisplay(value) : null}
				placeholder={
					suggestion ? `Suggested: ${toDisplay(suggestion.value)}` : undefined
				}
				onChange={(v) => {
					if (v === null) {
						onChange(null);
						return;
					}
					const displayValue = Number(v);
					if (positiveOnly && displayValue <= 0) {
						return;
					}
					onChange(fromDisplay(displayValue));
				}}
			/>
		);

		return (
			<>
				<FieldLabel field={field} hint={hint} />
				{showSuggestionAddon ? (
					<Space.Compact block style={{ width: '100%' }}>
						{inputNumber}
						{matchesSuggestion ? (
							<Button
								disabled
								aria-label="Matches suggested value"
								icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
							/>
						) : (
							<Button onClick={onApplySuggestion}>Use Suggested</Button>
						)}
					</Space.Compact>
				) : (
					inputNumber
				)}
				<IssueText issue={issue} value={value} />
			</>
		);
	}

	return (
		<>
			<FieldLabel field={field} />
			<Input
				readOnly={readOnly}
				status={issueStatus}
				value={value === null || value === undefined ? '' : String(value)}
				placeholder="—"
				onChange={(e) => onChange(e.target.value)}
			/>
			<IssueText issue={issue} value={value} />
		</>
	);
};

interface RobotProgramViewModalProps {
	orderLineItemId: string;
	program: VSCPartData | null;
	/** `program` is an unsaved template; Save creates it instead of updating. */
	isNew?: boolean;
	/** The line item's programs, so a new one can't take an existing key. */
	siblings?: VSCPartData[];
	useInches: boolean;
	readOnly?: boolean;
	onChangeUnits: (useInches: boolean) => void;
	onClose: () => void;
}

const RobotProgramViewModal = ({
	orderLineItemId,
	program,
	isNew = false,
	siblings = [],
	useInches,
	readOnly = false,
	onChangeUnits,
	onClose,
}: RobotProgramViewModalProps) => {
	const [messageApi, messageContext] = message.useMessage();
	const [modal, modalContext] = Modal.useModal();
	const [search, setSearch] = useState('');
	const [collapsedSections, setCollapsedSections] = useState<string[]>([]);
	const [draft, setDraft] = useState<VSCPartJson>({});
	const [updateVSCPartData, { isLoading: isUpdating }] =
		useUpdateVSCPartDataMutation();
	const [createVSCPartData, { isLoading: isCreating }] =
		useCreateVSCPartDataMutation();
	const isLoading = isUpdating || isCreating;

	useEffect(() => {
		if (program) {
			setDraft(structuredClone(program.json_data ?? {}));
		}
	}, [program]);

	const original = useMemo(() => program?.json_data ?? {}, [program]);
	const sections = useMemo(
		() => resolveSections(draft, original),
		[draft, original]
	);

	const [checkedDraft, setCheckedDraft] = useState<VSCPartJson | null>(null);
	useEffect(() => {
		if (!program) return;
		const timer = setTimeout(() => setCheckedDraft(draft), CHECK_DELAY_MS);
		return () => clearTimeout(timer);
	}, [draft, program]);
	const { data: check, isError: checkFailed } = useCheckVSCPartDataQuery(
		checkedDraft ?? {},
		{ skip: !checkedDraft }
	);
	const auditIssues = useMemo(() => check?.findings ?? [], [check]);
	const issuesByField = useMemo(
		() => new Map(auditIssues.map((issue) => [issue.field, issue])),
		[auditIssues]
	);

	const hasChanges = JSON.stringify(draft) !== JSON.stringify(original);
	const keyChanged =
		!isNew &&
		(draft.name !== original.name || draft.description !== original.description);
	const emptyNumbers = sections
		.flatMap((section) => section.rows.flat())
		.filter((field) => field.value === null)
		.filter((field) => {
			if (isNew && !field.root && REQUIRED_NEW_PROGRAM_KEYS.has(field.key)) {
				return true;
			}
			const source = field.root
				? original
				: ((original.parameters ?? {}) as Record<string, unknown>);
			return typeof source[field.key] === 'number';
		});
	const missingName = !String(draft.name ?? '').trim();
	const missingLabels = [
		...(missingName ? ['Part Number'] : []),
		...emptyNumbers.map((field) => field.label),
	];
	// Same key as the server's upsert: the line item plus name and description.
	const keyTaken =
		isNew &&
		siblings.some(
			(sibling) =>
				(sibling.json_data?.name ?? null) === (draft.name ?? null) &&
				(sibling.json_data?.description ?? null) === (draft.description ?? null)
		);
	const canSave =
		(isNew || hasChanges) && missingLabels.length === 0 && !keyTaken;

	const updateField = (field: ResolvedField, value: unknown) => {
		setDraft((current) => {
			if (field.root && field.key === 'op_count' && typeof value === 'number') {
				return { ...current, op_count: value, jaws: resizeJaws(current, value) };
			}
			if (field.root) {
				return { ...current, [field.key]: value };
			}
			const next: VSCPartJson = {
				...current,
				parameters: { ...(current.parameters ?? {}), [field.key]: value },
			};
			const jawIndex = JAW_KEY_INDEX[field.key];
			if (jawIndex !== undefined && Array.isArray(current.jaws)) {
				const jaws = [...current.jaws];
				if (jawIndex < jaws.length) {
					jaws[jawIndex] = value as string;
					next.jaws = jaws;
				}
			}
			return next;
		});
	};

	const handleAutofill = (values: CamStockValues, source: string) => {
		setDraft((current) => ({
			...current,
			parameters: { ...(current.parameters ?? {}), ...values },
		}));
		const labels: Record<keyof CamStockValues, string> = {
			width: 'Width',
			length: 'Length',
			partHeight: 'Height',
		};
		const filled = (Object.keys(labels) as (keyof CamStockValues)[])
			.filter((key) => values[key] !== undefined)
			.map(
				(key) =>
					`${labels[key]} ${formatLength(key, values[key], useInches)} ${useInches ? 'in' : 'mm'
					}`
			);
		const skipped = (Object.keys(labels) as (keyof CamStockValues)[])
			.filter((key) => values[key] === undefined)
			.map((key) => labels[key]);
		messageApi.success(
			`Stock ${filled.join(', ')} filled from ${source}${skipped.length ? ` (no ${skipped.join(' or ')} in the CAM stock)` : ''
			}`
		);
	};

	const handleClose = () => {
		if (!hasChanges || isLoading) {
			if (!isLoading) onClose();
			return;
		}
		modal.confirm({
			title: 'Discard unsaved changes?',
			okText: 'Discard',
			okButtonProps: { danger: true },
			onOk: onClose,
		});
	};

	const handleSave = async () => {
		if (!program || !canSave) {
			return;
		}
		try {
			if (isNew) {
				await createVSCPartData({ orderLineItemId, json_data: draft }).unwrap();
			} else {
				await updateVSCPartData({
					id: program.id,
					orderLineItemId,
					json_data: draft,
				}).unwrap();
			}
			messageApi.success(isNew ? 'Robot program created' : 'Robot program saved');
			onClose();
		} catch (e) {
			messageApi.error(extractApiError(e));
		}
	};

	const query = search.trim().toLowerCase();
	const visibleSections = query
		? sections
			.map((section) => ({
				...section,
				rows: section.rows
					.map((row) =>
						row.filter(
							(f) =>
								f.label.toLowerCase().includes(query) ||
								f.key.toLowerCase().includes(query)
						)
					)
					.filter((row) => row.length > 0),
			}))
			.filter((section) => section.rows.length > 0)
		: sections;
	const titledSections = visibleSections.filter(
		(section): section is ResolvedSection & { title: string } => !!section.title
	);

	const fieldId = (field: Pick<ResolvedField, 'root' | 'key'>) =>
		`${field.root}-${field.key}`;
	// Counted on each panel header so a collapsed panel can't hide a problem.
	const problemFields = new Set([
		...auditIssues.map((issue) => fieldId({ root: false, key: issue.field })),
		...emptyNumbers.map(fieldId),
	]);

	const yPushSuggestion = check?.y_push_height_suggestion ?? null;

	const renderRows = (section: ResolvedSection) =>
		section.rows.map((row, rowIndex) => (
			<div
				key={rowIndex}
				className="grid gap-x-4 gap-y-3 items-start mb-3 max-md:!grid-cols-1"
				style={{ gridTemplateColumns: rowColumns(row) }}
			>
				{row.map((field) => (
					<div key={fieldId(field)}>
						<FieldInput
							field={field}
							useInches={useInches}
							readOnly={readOnly}
							issue={field.root ? undefined : issuesByField.get(field.key)}
							suggestion={
								!field.root && field.key === 'yPushHeight'
									? yPushSuggestion
									: undefined
							}
							onApplySuggestion={
								!field.root &&
									field.key === 'yPushHeight' &&
									yPushSuggestion !== null
									? () => updateField(field, yPushSuggestion.value)
									: undefined
							}
							onChange={(value) => updateField(field, value)}
						/>
					</div>
				))}
			</div>
		));

	return (
		<>
			<Modal
				title={
					isNew
						? 'New Robot Program'
						: `Robot Program${original.name ? ` — ${original.name}` : ''}`
				}
				open={!!program}
				onCancel={handleClose}
				maskClosable={false}
				afterClose={() => {
					setSearch('');
					setCollapsedSections([]);
					setCheckedDraft(null);
				}}
				footer={
					<Flex justify="end" gap="small">
						<Button onClick={handleClose} disabled={isLoading}>
							{readOnly ? 'Close' : 'Cancel'}
						</Button>
						{!readOnly && (
							<Button
								type="primary"
								loading={isLoading}
								disabled={!canSave}
								onClick={handleSave}
							>
								{isNew ? 'Create' : 'Save'}
							</Button>
						)}
					</Flex>
				}
				width={1100}
				centered
			>
				{/* One scroll area sized to the window, so banners scroll with the
				    form instead of pushing the modal past the bottom of the screen. */}
				<div
					className="overflow-y-auto pr-2"
					style={{ maxHeight: 'calc(100vh - 240px)' }}
				>
					{checkFailed && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 16 }}
							message="Couldn't check this program against the audit rules"
						/>
					)}
					{auditIssues.length > 0 && (
						<Alert
							type={
								auditIssues.some((issue) => issue.severity === 'error')
									? 'error'
									: 'warning'
							}
							showIcon
							style={{ marginBottom: 16 }}
							message={`This program fails ${auditIssues.length} audit ${auditIssues.length === 1 ? 'check' : 'checks'
								}`}
							description={
								<ul className="list-disc pl-5 m-0">
									{auditIssues.map((issue) => (
										<li key={issue.field}>{issue.message}</li>
									))}
								</ul>
							}
						/>
					)}
					{keyTaken && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 16 }}
							message="Choose a different Part Number or Description"
							description="A robot program with this Part Number and Description already exists on this line item, and the next Fusion 360 export would overwrite one of them."
						/>
					)}
					{keyChanged && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 16 }}
							message="Part Number or Description changed"
							description="The next Fusion 360 export of the original program will create a new robot program instead of updating this one."
						/>
					)}
					<Flex
						justify="space-between"
						align="center"
						gap="middle"
						wrap="wrap"
						style={{
							position: 'sticky',
							top: 0,
							zIndex: 2,
							background: '#fff',
							paddingBottom: 12,
							marginBottom: 4,
						}}
					>
						<Input
							allowClear
							prefix={<SearchOutlined />}
							placeholder="Filter parameters"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							style={{ maxWidth: 320 }}
						/>
						<Flex align="center" gap="middle" wrap="wrap">
							{!readOnly && program && (
								<CamStockAutofill
									orderLineItemId={orderLineItemId}
									disabled={isLoading}
									onApply={handleAutofill}
								/>
							)}
							<UnitSwitch useInches={useInches} onChange={onChangeUnits} />
						</Flex>
					</Flex>
					{!isNew && missingLabels.length > 0 && (
						<Alert
							type="error"
							showIcon
							style={{ marginBottom: 12 }}
							message={`Enter a value for ${missingLabels.join(', ')}`}
						/>
					)}
					{visibleSections.length === 0 ? (
						<Empty description="No parameters match" />
					) : (
						<>
							{visibleSections
								.filter((section) => !section.title)
								.map((section, sectionIndex) => (
									<div key={sectionIndex}>{renderRows(section)}</div>
								))}
							<Collapse
								ghost
								activeKey={
									query
										? titledSections.map((section) => section.title)
										: titledSections
											.map((section) => section.title)
											.filter((title) => !collapsedSections.includes(title))
								}
								onChange={(keys) => {
									if (query) return;
									const open = Array.isArray(keys) ? keys : [keys];
									setCollapsedSections(
										titledSections
											.map((section) => section.title)
											.filter((title) => !open.includes(title))
									);
								}}
								items={titledSections.map((section) => {
									const problems = section.rows
										.flat()
										.filter((field) => problemFields.has(fieldId(field))).length;
									return {
										key: section.title,
										label: (
											<span style={{ fontWeight: 700 }}>
												{section.title}
											</span>
										),
										extra:
											problems > 0 ? (
												<Tag color="error" style={{ marginInlineEnd: 0 }}>
													{problems} {problems === 1 ? 'issue' : 'issues'}
												</Tag>
											) : undefined,
										styles: {
											header: { background: '#f5f5f5', borderRadius: 6 },
										},
										children: renderRows(section),
									};
								})}
								style={{ marginTop: 8 }}
							/>
						</>
					)}
				</div>
			</Modal>
			{messageContext}
			{modalContext}
		</>
	);
};

export default RobotProgramViewModal;
